import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getCustomersByBootcamp, getUsers, getCurrentCamp, getBootcampActivity, getRaceState } from '../actions/api';
import { getCurrentUser } from '../actions/auth';
import { TEAM_LIVERY } from '../theme';
import { tally, REFERRAL_POINTS } from '../scoring';

const REFRESH_MS = 15000;
const BADGE_MS = 60000;
const HIT_MS = 2400;
const VIEW_KEY = 'tnp.scoreboardView';

const COLUMNS = [
  { key: 'qc', label: 'Quality conversations' },
  { key: 'appset', label: 'Appts set' },
  { key: 'appshown', label: 'Shown' },
  { key: 'referrals', label: 'Referrals' },
  { key: 'sold', label: 'Sold' },
];

// Counts that animate when they go up between refreshes. Standings celebrates the four below;
// the funnel animates every stage.
const HIT_KEYS = ['customers', 'contacted', 'qc', 'appset', 'appshown', 'sold', 'referrals'];
const STANDINGS_HITS = { appset: 'Appt set', appshown: 'Shown', referrals: 'Referral', sold: 'Sold' };
const hitLabel = (key, n) => `+${n} ${STANDINGS_HITS[key]}${key === 'referrals' && n > 1 ? 's' : ''}`;

// Funnel board steps, top to bottom; each converts from the one above it.
const FUNNEL_STAGES = [
  { key: 'qc', label: 'Quality conversations' },
  { key: 'appset', label: 'Appointments set' },
  { key: 'appshown', label: 'Shown' },
  { key: 'sold', label: 'Sold' },
];

// Callouts under the funnel board: the team with the best rate into each step.
const FUNNEL_BESTS = [
  { key: 'appset', title: 'Best at booking', text: (team, r) => `${team} sets an appointment from ${r}% of conversations` },
  { key: 'appshown', title: 'Best show rate', text: (team, r) => `${team} gets ${r}% of appointments in the door` },
  { key: 'sold', title: 'Best closer', text: (team, r) => `${team} sells ${r}% of shown appointments` },
];

// Race control wording for each kind of logged activity.
const ACTIVITY_TEXT = {
  contacted: () => 'contacted a customer',
  qc: () => 'had a quality conversation',
  appset: () => 'set an appointment',
  appshown: () => 'had an appointment show',
  sold: () => 'sold a car',
  referrals: (n) => (n > 1 ? `got ${n} referrals` : 'got a referral'),
};

const formatEventTime = (at) => {
  const d = new Date(at);
  const sameDay = d.toDateString() === new Date().toDateString();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return sameDay ? time : `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time}`;
};

const activityEvent = (a) => {
  const team = TEAM_LIVERY[a.teamColor];
  const who = a.rep ? `${a.rep} (${team ? team.label : 'No team'})` : `${team ? team.label : 'A'} team`;
  return {
    id: a.id,
    color: a.teamColor,
    at: a.at,
    text: `${who} ${(ACTIVITY_TEXT[a.kind] || (() => 'logged an update'))(a.count)}`,
    points: a.points,
  };
};

// Car paint per team; Black gets dark grey with silver so it shows on the dark track.
const CAR_PAINT = {
  red: { body: '#D7263D', accent: '#FFFFFF', stripe: '#D7263D' },
  blue: { body: '#1D5FD6', accent: '#FFFFFF', stripe: '#1D5FD6' },
  green: { body: '#15935B', accent: '#FFFFFF', stripe: '#15935B' },
  black: { body: '#3A4049', accent: '#AEB6BF', stripe: '#AEB6BF' },
};

const reducedMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const readView = () => {
  try {
    return window.localStorage.getItem(VIEW_KEY) || 'standings';
  } catch (e) {
    return 'standings';
  }
};

const buildTeams = (customers, rosters) =>
  Object.keys(TEAM_LIVERY)
    .map((color) => ({
      color,
      label: TEAM_LIVERY[color].label,
      members: (rosters[`${color}Team`] || []).length,
      ...tally(customers.filter((c) => c.teamColor === color)),
    }))
    .filter((t) => t.members > 0 || t.customers > 0)
    .sort((a, b) => b.points - a.points);

// During a sprint the race board ranks teams by points scored since the sprint started.
const sprintTeams = (teams, sprint) =>
  teams
    .map((t) => ({ ...t, total: t.points, points: (sprint.teams && sprint.teams[t.color]) || 0 }))
    .sort((a, b) => b.points - a.points || b.total - a.total);

const formatClock = (ms) => {
  const total = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = String(total % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
};

const SprintBar = ({ sprint, lastSprint, active, now }) => {
  if (sprint) {
    return (
      <div className="sprint-strip">
        <span className="sprint-flag" aria-hidden="true" />
        <span className="sprint-goal">
          {sprint.targetPoints ? `First to ${sprint.targetPoints} points` : 'Most points wins'}
          {sprint.endsAt && sprint.targetPoints ? ', or most when time runs out' : ''}. Only points scored in this sprint count.
        </span>
        {sprint.endsAt && <span className="sprint-clock" aria-label="Time left">{formatClock(new Date(sprint.endsAt) - now)}</span>}
      </div>
    );
  }
  if (lastSprint) {
    const winner = lastSprint.winnerColor && TEAM_LIVERY[lastSprint.winnerColor];
    return (
      <div className="sprint-strip finished">
        <span className="sprint-flag" aria-hidden="true" />
        <span className="sprint-name">
          {winner
            ? `${winner.label} wins the Sprint Race with ${lastSprint.teams[lastSprint.winnerColor]} points`
            : 'The Sprint Race ended in a tie'}
        </span>
      </div>
    );
  }
  if (!active) {
    return <div className="sprint-strip paused"><span className="sprint-name">Race paused</span><span className="sprint-goal">Scores still count. Race control will restart the race.</span></div>;
  }
  return null;
};

const Badge = ({ badge }) => (
  <span className={`move-badge ${badge.dir}`} key={badge.seq}>
    <span className="move-badge-text">
      <svg width="0.8em" height="0.8em" viewBox="0 0 10 10" aria-hidden="true">
        <path d={badge.dir === 'up' ? 'M5 1l4 7H1z' : 'M5 9L1 2h8z'} fill="currentColor" />
      </svg>
      {badge.dir === 'up' ? 'Up' : 'Down'} {badge.n}
    </span>
    <span className="move-badge-timer" />
  </span>
);

// Top-down open-wheel car (the TNP Race Mode redesign), nose pointing right.
export const RaceCar = ({ color, number }) => {
  const p = CAR_PAINT[color];
  return (
    <svg viewBox="0 0 260 100" aria-hidden="true">
      <path d="M58 21 L150 18 Q178 20 188 37 L188 63 Q178 80 150 82 L58 79 Q50 50 58 21 Z" fill="#12161B" />
      <path d="M40 26 L50 41 M62 26 L72 39 M40 74 L50 59 M62 74 L72 61 M192 25 L186 45 M208 25 L214 46 M192 75 L186 55 M208 75 L214 54"
        fill="none" stroke="#0B0D10" strokeWidth="2.5" strokeLinecap="round" />
      <rect x="26" y="0" width="48" height="26" rx="9" fill="#0B0D10" />
      <rect x="26" y="74" width="48" height="26" rx="9" fill="#0B0D10" />
      <rect x="180" y="3" width="40" height="22" rx="8" fill="#0B0D10" />
      <rect x="180" y="75" width="40" height="22" rx="8" fill="#0B0D10" />
      <rect x="34" y="4" width="32" height="3" rx="1.5" fill="#fff" opacity="0.1" />
      <rect x="34" y="93" width="32" height="3" rx="1.5" fill="#fff" opacity="0.1" />
      <rect x="187" y="6" width="26" height="3" rx="1.5" fill="#fff" opacity="0.1" />
      <rect x="187" y="91" width="26" height="3" rx="1.5" fill="#fff" opacity="0.1" />
      <rect x="8" y="15" width="18" height="70" rx="4" fill={p.body} />
      <rect x="17" y="18" width="4" height="64" rx="2" fill={p.accent} opacity="0.9" />
      <rect x="3" y="11" width="30" height="5" rx="2.5" fill="#0B0D10" />
      <rect x="3" y="84" width="30" height="5" rx="2.5" fill="#0B0D10" />
      <path d="M246 46 Q244 20 236 7 L224 7 Q232 24 232 46 Z" fill={p.body} />
      <path d="M246 54 Q244 80 236 93 L224 93 Q232 76 232 54 Z" fill={p.body} />
      <path d="M239 44 Q238 23 231 10 M239 56 Q238 77 231 90" fill="none" stroke={p.accent} strokeWidth="2" strokeLinecap="round" />
      <rect x="220" y="4" width="20" height="4" rx="2" fill="#0B0D10" />
      <rect x="220" y="92" width="20" height="4" rx="2" fill="#0B0D10" />
      <path d="M24 44 L60 42 C84 40 100 25 150 23 Q160 23 161 31 L163 40 L236 45 Q250 46 250 50 Q250 54 236 55 L163 60 L161 69 Q160 77 150 77 C100 75 84 60 60 58 L24 56 Z"
        fill={p.body} />
      <path d="M60 42 C84 40 100 25 150 23 Q160 23 161 31 L162 36 C120 34 96 44 60 46 Z" fill="#000" opacity="0.2" />
      <path d="M60 58 C84 60 100 75 150 77 Q160 77 161 69 L162 64 C120 66 96 56 60 54 Z" fill="#000" opacity="0.2" />
      <path d="M28 47 L112 44 L112 56 L28 53 Z" fill="#fff" opacity="0.12" />
      <rect x="152" y="26" width="6" height="10" rx="2" fill="#0B0D10" />
      <rect x="152" y="64" width="6" height="10" rx="2" fill="#0B0D10" />
      <rect x="30" y="49" width="48" height="2" rx="1" fill={p.accent} opacity="0.9" />
      <rect x="166" y="49" width="74" height="2" rx="1" fill={p.accent} opacity="0.9" />
      <rect x="21" y="47" width="4" height="6" rx="1" fill="#FF2A3A" />
      <ellipse cx="138" cy="50" rx="17" ry="8" fill="#0B0D10" />
      <circle cx="135" cy="50" r="5.5" fill={p.accent} />
      <path d="M124 42.5 Q150 40 160 50 Q150 60 124 57.5 M160 50 L167 50" fill="none" stroke={p.accent} strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="94" cy="50" r="12" fill="#fff" />
      <text x="94" y="55.5" textAnchor="middle" fontFamily="Barlow Condensed, sans-serif" fontWeight="800"
        fontSize="16" fill="#1C2127">{number}</text>
    </svg>
  );
};

// Appointments set and shown, referrals and sales each light up their cell with a "+1".
// A sale also sweeps a checkered flag across the row and stamps it SOLD.
const StandingsView = ({ teams, badges, bumps, hits, flash }) => (
  <ol className="standings" aria-label="Team standings">
    {teams.map((t, i) => {
      const rowHits = Object.keys(STANDINGS_HITS).filter((k) => hits[`${t.color}:${k}`]);
      return (
      <li key={t.color} className={`standing${i === 0 && t.points > 0 ? ' leader' : ''}`}
        style={{ top: `${(i * 100) / teams.length}%`, height: `calc(${100 / teams.length}% - 10px)` }}
        aria-label={`Position ${i + 1}: ${t.label} team, ${t.points} points`}>
        {!!flash[t.color] && (
          <>
            <div className="flag-sweep" key={`f${flash[t.color]}`} />
            <div className="sold-stamp" key={`s${flash[t.color]}`} aria-hidden="true"><span>Sold!</span></div>
          </>
        )}
        <div className="standing-pos">P{i + 1}</div>
        <div className="standing-livery" style={{ background: CAR_PAINT[t.color].stripe }} />
        <div className="standing-team">
          <div className="standing-name">
            <span>{t.label}</span>
            {badges[t.color] && <Badge badge={badges[t.color]} />}
            {rowHits.map((k) => {
              const h = hits[`${t.color}:${k}`];
              return <span key={`${k}${h.seq}`} className={`hit-chip hit-${k}`}>{hitLabel(k, h.n)}</span>;
            })}
          </div>
          <small>{t.members} {t.members === 1 ? 'rep' : 'reps'}, {t.customers} customers</small>
        </div>
        {COLUMNS.map((c) => {
          const h = STANDINGS_HITS[c.key] && hits[`${t.color}:${c.key}`];
          return (
            <div key={h ? `${c.key}${h.seq}` : c.key} className={`standing-stat${h ? ` stat-hit hit-${c.key}` : ''}`}>
              {h && <span className="stat-pop" aria-hidden="true">+{h.n}</span>}
              <span className="n">{t[c.key]}</span>
              <span className="l">{c.label}</span>
            </div>
          );
        })}
        <div className="standing-points">
          <span className={`n${bumps[t.color] ? ` bump${bumps[t.color] % 2 ? 'A' : 'B'}` : ''}`}>{t.points}</span>
          <span className="l">points</span>
        </div>
      </li>
      );
    })}
  </ol>
);

// Percent of a team's previous funnel step that reached this one, or null with nothing to convert.
// Reps can tick a later stage without the earlier one, so the count can outrun its step; cap at 100.
const convRate = (t, key) => {
  const i = FUNNEL_STAGES.findIndex((s) => s.key === key);
  const from = t[FUNNEL_STAGES[i - 1].key];
  return from > 0 ? Math.min(100, Math.round((t[key] / from) * 100)) : null;
};

// One funnel column per team, in standings order. Bars share one scale across teams; the best
// conversion into each step is marked. A newly logged count pops a "+1" over its bar.
const FunnelView = ({ teams, hits, flash, bumps }) => {
  const widest = Math.max(1, ...teams.flatMap((t) => FUNNEL_STAGES.map((s) => t[s.key])));
  const best = {};
  const bests = [];
  FUNNEL_STAGES.slice(1).forEach((s) => {
    let top = null;
    teams.forEach((t) => {
      const r = convRate(t, s.key);
      if (r !== null && r > 0 && (!top || r > top.r)) top = { t, r };
    });
    best[s.key] = top ? top.r : null;
    const callout = FUNNEL_BESTS.find((b) => b.key === s.key);
    if (top) bests.push({ ...callout, color: top.t.color, line: callout.text(top.t.label, top.r) });
  });
  return (
    <div className="tf-board">
      <ol className="tf-teams" aria-label="Team funnels">
        {teams.map((t, i) => (
          <li key={t.color} className={`tf-col${i === 0 && t.points > 0 ? ' leader' : ''}`}
            style={{ '--livery': CAR_PAINT[t.color].stripe }}
            aria-label={`Position ${i + 1}: ${t.label} team, ${t.points} points`}>
            {!!flash[t.color] && <div className="flag-sweep" key={flash[t.color]} />}
            <div className="tf-head">
              <div>
                <span className="tf-pos">P{i + 1}</span>
                <span className="tf-name">{t.label}</span>
              </div>
              <div className="tf-points">
                <span className={`n${bumps[t.color] ? ` bump${bumps[t.color] % 2 ? 'A' : 'B'}` : ''}`}>{t.points}</span>
                <span className="l">points</span>
              </div>
            </div>
            <div className="tf-steps">
              {FUNNEL_STAGES.map((s, si) => {
                const n = t[s.key] || 0;
                const r = si > 0 ? convRate(t, s.key) : null;
                const h = hits[`${t.color}:${s.key}`];
                return (
                  <div key={s.key} className="tf-step">
                    {si > 0 && (
                      <span className={`tf-rate${r !== null && r === best[s.key] ? ' best' : ''}`}>
                        <svg width="0.7em" height="0.7em" viewBox="0 0 12 12" aria-hidden="true"><path d="M6 11L1 4h10z" fill="currentColor" /></svg>
                        {r === null ? '—' : `${r}%`} converted
                      </span>
                    )}
                    <div className="tf-step-head">
                      <span>{s.label}</span>
                      <b key={h ? h.seq : 'n'} className={h ? 'hit' : ''}>{n}</b>
                    </div>
                    <div className="tf-track">
                      <div key={h ? h.seq : 'f'} className={`tf-fill${h ? ' seg-hit' : ''}`}
                        style={{ width: `${n ? Math.max((n / widest) * 100, 6) : 0}%`, opacity: 1 - si * 0.12 }} />
                      {h && <span key={h.seq} className={`tf-pop hit-${s.key}`} aria-hidden="true">+{h.n}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </li>
        ))}
      </ol>

      {bests.length > 0 && (
        <ul className="tf-bests">
          {bests.map((b) => (
            <li key={b.key}>
              <span className="tf-best-stripe" style={{ background: CAR_PAINT[b.color].stripe }} />
              <span><b>{b.title}:</b> {b.line}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const RaceView = ({ teams, badges, bumps, started, lit, showLights, moving, flash, events, sprint, paused }) => {
  const leader = teams[0] ? teams[0].points : 0;
  // In a target sprint the finish line is the target; otherwise the leader sets the pace.
  const scale = Math.max((sprint && sprint.targetPoints) || leader, 1);
  const laneOrder = Object.keys(TEAM_LIVERY).filter((c) => teams.some((t) => t.color === c));
  return (
    <div className="race">
      <ol className="race-tower" aria-label="Team positions">
        {teams.map((t, i) => (
          <li key={t.color} className="tower-row"
            style={{ top: `${(i * 100) / teams.length}%`, height: `calc(${100 / teams.length}% - 10px)` }}>
            <div className="tower-pos" style={{ color: i === 0 ? '#fff' : 'rgba(255,255,255,0.45)' }}>P{i + 1}</div>
            <div className="tower-team">
              <div className="tower-name">
                <span className="tower-stripe" style={{ background: CAR_PAINT[t.color].stripe }} />
                <span>{t.label}</span>
              </div>
              <div className="tower-meta">
                <span>{i === 0 ? 'Leader' : `−${leader - t.points} pts`}{sprint ? `, ${t.total} total` : ''}</span>
                {badges[t.color] && <Badge badge={badges[t.color]} />}
              </div>
            </div>
            <div className={`tower-points${i === 0 ? ' leader' : ''}`}>
              <span className={bumps[t.color] ? `bump${bumps[t.color] % 2 ? 'A' : 'B'}` : ''}>{t.points}</span>
            </div>
          </li>
        ))}
      </ol>

      <div className="race-main">
        <div className="track">
          <div className="finish-line" />
          {laneOrder.map((color) => {
            const t = teams.find((x) => x.color === color);
            const pos = teams.indexOf(t) + 1;
            return (
              <div key={color} className="lane">
                <span className="lane-name">{t.label}</span>
                {!!flash[color] && !paused && <div className="flag-sweep" key={flash[color]} />}
                <div className="car" style={{ '--progress': started ? Math.min(t.points / scale, 1) : 0 }}
                  role="img" aria-label={`${t.label} team car, position ${pos}`}>
                  {(moving[color] || !started) && !showLights && !paused && <div className="speed-lines" />}
                  <RaceCar color={color} number={pos} />
                </div>
              </div>
            );
          })}
          {showLights && (
            <div className="lights-overlay">
              <div className="lights">
                {[0, 1, 2, 3, 4].map((i) => <span key={i} className={`light${i < lit ? ' on' : ''}`} />)}
              </div>
            </div>
          )}
        </div>
        <div className="race-control" aria-live="polite">
          {events.length === 0 ? (
            <span className="race-control-empty">
              Latest: every appointment, show and sale lands here as reps log it.
            </span>
          ) : events.map((e, i) => (
            <div key={e.id} className={`race-event${i === 0 ? ' latest' : ''}`}>
              <span className="race-event-stripe" style={{ background: (CAR_PAINT[e.color] || CAR_PAINT.black).stripe }} />
              <span className="race-event-time">{formatEventTime(e.at)}</span>
              <span style={{ fontWeight: e.bold ? 700 : 500 }}>{e.text}</span>
              {!!e.points && (
                <span className="race-event-points" aria-label={`${e.points > 0 ? 'plus' : 'minus'} ${Math.abs(e.points)} points`}
                  style={{ background: (CAR_PAINT[e.color] || CAR_PAINT.black).body, borderColor: (CAR_PAINT[e.color] || CAR_PAINT.black).stripe }}>
                  {e.points > 0 ? `+${e.points}` : `−${Math.abs(e.points)}`}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Scoreboard = () => {
  const isAdmin = (getCurrentUser() || {}).role === 'admin';
  const [view, setViewState] = useState(isAdmin ? readView() : 'standings');
  const [teams, setTeams] = useState(null);
  const [campName, setCampName] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [badges, setBadges] = useState({});
  const [bumps, setBumps] = useState({});
  const [moving, setMoving] = useState({});
  const [flash, setFlash] = useState({});
  const [hits, setHits] = useState({});
  const [overtakes, setOvertakes] = useState([]);
  const [activity, setActivity] = useState([]);
  const [lights, setLights] = useState({ started: true, lit: 0, show: false });
  const [race, setRace] = useState({ active: true, sprint: null, lastSprint: null });
  const [now, setNow] = useState(Date.now());
  const [isFullscreen, setIsFullscreen] = useState(false);

  const rootRef = useRef(null);
  const prevTeams = useRef(null);
  const viewRef = useRef(view);
  const sprintIdRef = useRef(undefined);
  const badgeTimers = useRef({});
  const hitTimers = useRef({});
  const hitSeq = useRef(0);
  const timers = useRef([]);

  const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));

  const runLights = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    if (reducedMotion()) {
      setLights({ started: true, lit: 0, show: false });
      return;
    }
    setLights({ started: false, lit: 0, show: true });
    for (let i = 1; i <= 5; i++) later(() => setLights({ started: false, lit: i, show: true }), 500 * i);
    later(() => setLights({ started: true, lit: 0, show: false }), 500 * 5 + 900);
  }, []);

  const setView = (v) => {
    setViewState(v);
    viewRef.current = v;
    prevTeams.current = null;
    try {
      window.localStorage.setItem(VIEW_KEY, v);
    } catch (e) {
      // Remembering the view is a convenience only.
    }
    if (v === 'race' && race.active) runLights();
  };

  // Compare with the previous refresh: overtakes get 60-second badges, new activity
  // becomes Race control events and animations.
  // `mode` changes when a sprint starts or ends or the view switches; positions then start fresh.
  const applyUpdate = useCallback((next, mode) => {
    const prevEntry = prevTeams.current;
    prevTeams.current = { mode, list: next };
    if (!prevEntry || prevEntry.mode !== mode) return;
    const prev = prevEntry.list;

    const before = prev.map((t) => t.color);
    const after = next.map((t) => t.color);
    const newEvents = [];
    const at = new Date().toISOString();

    after.forEach((color) => {
      const delta = before.indexOf(color) - after.indexOf(color);
      if (before.indexOf(color) === -1 || delta === 0) return;
      const dir = delta > 0 ? 'up' : 'down';
      setBadges((b) => ({ ...b, [color]: { dir, n: Math.abs(delta), seq: ((b[color] && b[color].seq) || 0) + 1 } }));
      clearTimeout(badgeTimers.current[color]);
      badgeTimers.current[color] = setTimeout(() => {
        setBadges((b) => {
          const copy = { ...b };
          delete copy[color];
          return copy;
        });
      }, BADGE_MS);
      if (dir === 'up') {
        newEvents.push({
          id: `o${at}${color}`, color, at, bold: true,
          text: `${TEAM_LIVERY[color].label} team moves up to P${after.indexOf(color) + 1}`,
        });
      }
    });

    next.forEach((t) => {
      const old = prev.find((p) => p.color === t.color);
      if (!old || t.points === old.points) return;
      setBumps((b) => ({ ...b, [t.color]: (b[t.color] || 0) + 1 }));
      setMoving((m) => ({ ...m, [t.color]: true }));
      later(() => setMoving((m) => ({ ...m, [t.color]: false })), 1350);
      if (t.sold > old.sold) {
        setFlash((f) => ({ ...f, [t.color]: Date.now() }));
        later(() => setFlash((f) => ({ ...f, [t.color]: 0 })), 1850);
      }
    });

    // Per-count highlights. Each keeps its own timer so a second sale restarts only that one.
    next.forEach((t) => {
      const old = prev.find((p) => p.color === t.color);
      if (!old) return;
      HIT_KEYS.forEach((k) => {
        const n = (t[k] || 0) - (old[k] || 0);
        if (n <= 0) return;
        const id = `${t.color}:${k}`;
        hitSeq.current += 1;
        setHits((h) => ({ ...h, [id]: { n, seq: hitSeq.current } }));
        clearTimeout(hitTimers.current[id]);
        hitTimers.current[id] = setTimeout(() => {
          setHits((h) => {
            const copy = { ...h };
            delete copy[id];
            return copy;
          });
        }, HIT_MS);
      });
    });

    if (newEvents.length) setOvertakes((list) => [...newEvents, ...list].slice(0, 2));
  }, []);

  useEffect(() => {
    let alive = true;
    let timer;
    const load = async () => {
      try {
        const [customers, rosters, recent, raceState] = await Promise.all([
          getCustomersByBootcamp(), getUsers(), getBootcampActivity(2).catch(() => null),
          getRaceState().catch(() => null),
        ]);
        if (!alive) return;
        if (recent) setActivity(recent.map(activityEvent));
        const next = buildTeams(customers, rosters || {});
        const rs = raceState || { active: true, sprint: null, lastSprint: null };
        setRace(rs);
        const inRace = isAdmin && viewRef.current === 'race';
        const sprint = inRace ? rs.sprint : null;
        // A new sprint gets its own start lights.
        const sprintId = rs.sprint ? rs.sprint.id : null;
        if (inRace && sprintIdRef.current !== undefined && sprintId && sprintId !== sprintIdRef.current && rs.active) {
          runLights();
        }
        sprintIdRef.current = sprintId;
        applyUpdate(sprint ? sprintTeams(next, sprint) : next, inRace ? `race:${sprint ? sprint.id : ''}` : viewRef.current);
        setTeams(next);
        setUpdatedAt(new Date());
      } catch (e) {
        // Keep showing the last standings; try again next tick.
      }
      if (alive) timer = setTimeout(load, REFRESH_MS);
    };
    getCurrentCamp().then((me) => alive && setCampName(me.bootcampName || '')).catch(() => {});
    load();
    if (view === 'race') runLights();
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    const allTimers = badgeTimers.current;
    const allHitTimers = hitTimers.current;
    return () => {
      alive = false;
      clearTimeout(timer);
      clearInterval(tick);
      timers.current.forEach(clearTimeout);
      Object.values(allTimers).forEach(clearTimeout);
      Object.values(allHitTimers).forEach(clearTimeout);
      document.removeEventListener('fullscreenchange', onFs);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else if (rootRef.current && rootRef.current.requestFullscreen) rootRef.current.requestFullscreen();
  };

  const showRace = isAdmin && view === 'race';
  const showFunnel = isAdmin && view === 'funnel';
  const raceTeams = teams && showRace && race.sprint ? sprintTeams(teams, race.sprint) : teams;
  const sprintOn = showRace && !!race.sprint;

  // Sprint theme reaches the top bar too.
  useEffect(() => {
    document.body.classList.toggle('sprint-mode', sprintOn);
    return () => document.body.classList.remove('sprint-mode');
  }, [sprintOn]);
  // Race control always shows the two latest things that happened, logged or overtakes.
  const raceEvents = [...overtakes, ...activity]
    .sort((a, b) => new Date(b.at) - new Date(a.at))
    .slice(0, 2);

  return (
    <div className={`scoreboard${isFullscreen ? ' is-fullscreen' : ''}${sprintOn ? ' sprint-mode' : ''}`} ref={rootRef}>
      <div className="scoreboard-head">
        <div>
          <h1>{showRace ? (race.sprint ? 'Sprint Race' : 'Race board') : showFunnel ? 'Funnel' : 'Standings'}</h1>
          {campName && <p>{campName}</p>}
        </div>
        <div className="scoreboard-tools">
          {updatedAt && (
            <span className="refresh">
              <span className="live-dot" aria-hidden="true" />
              Live, updated {updatedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            </span>
          )}
          {isAdmin && (
            <label className="view-picker">
              View
              <select value={view} onChange={(e) => setView(e.target.value)}>
                <option value="standings">Standings</option>
                <option value="race">Race board</option>
                <option value="funnel">Funnel</option>
              </select>
            </label>
          )}
          <button type="button" className="board-button" onClick={toggleFullscreen}>
            {isFullscreen ? 'Exit full screen' : 'Full screen'}
          </button>
        </div>
      </div>

      {showRace && <SprintBar sprint={race.sprint} lastSprint={race.lastSprint} active={race.active} now={now} />}

      <div className="scoreboard-body">
        {teams === null ? (
          <p className="board-message">Loading standings…</p>
        ) : teams.length === 0 ? (
          <p className="board-message">No teams have been set up for this bootcamp yet.</p>
        ) : showRace ? (
          <RaceView teams={raceTeams} badges={badges} bumps={bumps} started={lights.started} lit={lights.lit}
            showLights={lights.show && race.active} moving={moving} flash={flash} events={raceEvents}
            sprint={race.sprint} paused={!race.active} />
        ) : showFunnel ? (
          <FunnelView teams={teams} hits={hits} flash={flash} bumps={bumps} />
        ) : (
          <StandingsView teams={teams} badges={badges} bumps={bumps} hits={hits} flash={flash} />
        )}
      </div>

      {!showRace && (
        <p className="scoring-key">
          Points: quality conversation 1, appointment set 2, shown 3, sold 5, each referral {REFERRAL_POINTS}.
        </p>
      )}
    </div>
  );
};

export default Scoreboard;
